import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../apiService/Core/countriesApi";
import type { AdminCountry, CountryPatch, CreateCountryBody } from "./types";

const bySort = (a: AdminCountry, b: AdminCountry) => a.sortOrder - b.sortOrder || a.alpha2.localeCompare(b.alpha2);

export const useCountries = () => {
  const [items, setItems] = useState<AdminCountry[]>([]);
  const [source, setSource] = useState<"db" | "fallback">("db");
  const [enforced, setEnforced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.listCountries();
      if (!alive.current) return;
      setItems([...r.items].sort(bySort));
      setSource(r.source);
      setEnforced(r.enforced);
    } catch (e) {
      if (alive.current) setLoadError(api.errorMessage(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setRowBusy = (alpha2: string, v: boolean) => setBusy((b) => ({ ...b, [alpha2]: v }));

  /** Optimistic update: the UI changes at once and is rolled back if the server rejects it. Throws on failure. */
  const patch = useCallback(async (alpha2: string, change: CountryPatch) => {
    let previous: AdminCountry | undefined;
    setItems((cur) => cur.map((c) => {
      if (c.alpha2 !== alpha2) return c;
      previous = c;
      return { ...c, ...change };
    }));
    setRowBusy(alpha2, true);
    try {
      const saved = await api.updateCountry(alpha2, change);
      if (alive.current) setItems((cur) => cur.map((c) => (c.alpha2 === alpha2 ? saved : c)).sort(bySort));
    } catch (e) {
      if (alive.current && previous) setItems((cur) => cur.map((c) => (c.alpha2 === alpha2 ? (previous as AdminCountry) : c)));
      throw e;
    } finally {
      if (alive.current) setRowBusy(alpha2, false);
    }
  }, []);

  const create = useCallback(async (body: CreateCountryBody) => {
    const saved = await api.createCountry(body);
    if (alive.current) setItems((cur) => [...cur.filter((c) => c.alpha2 !== saved.alpha2), saved].sort(bySort));
    return saved;
  }, []);

  /** When the collection is empty the backend serves built-in defaults. This writes them to the database. */
  const saveDefaults = useCallback(async () => {
    for (const c of items) {
      await api.createCountry({
        alpha2: c.alpha2, isActive: c.isActive, signupEnabled: c.signupEnabled, onboardingEnabled: c.onboardingEnabled,
        stateRequired: c.stateRequired, disabledStates: c.disabledStates, sortOrder: c.sortOrder,
      });
    }
    await load();
  }, [items, load]);

  return { items, source, enforced, loading, loadError, busy, reload: load, patch, create, saveDefaults };
};
